import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/app/AppLayout";
import api from "../lib/api";
import useTier from "../hooks/useTier";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useTier();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | timeout
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setError(t("billingSuccess.missingSession"));
      return;
    }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      if (cancelled) return;
      attempt++;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        setError(e.response?.data?.detail || t("billingSuccess.statusError"));
      }
      if (attempt >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh, t]);

  return (
    <AppLayout>
      <div className="px-8 py-16 max-w-2xl mx-auto text-center" data-testid="billing-success">
        {status === "polling" && (
          <div data-testid="status-polling">
            <Loader2 size={48} className="text-blue-400 mx-auto animate-spin mb-4"/>
            <h1 className="font-display font-bold text-2xl text-slate-100">{t("billingSuccess.verifying")}</h1>
            <p className="text-slate-400 mt-2">{t("billingSuccess.verifyingBody")}</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="status-paid">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-5">
              <CheckCircle2 size={40} className="text-emerald-400"/>
            </div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center justify-center gap-2">
              <Crown size={24} className="text-amber-400"/> {t("billingSuccess.welcome")}
            </h1>
            <p className="text-slate-400 mt-3 max-w-md mx-auto">
              {t("billingSuccess.welcomeBody")}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <button onClick={() => navigate("/dashboard")} className="btn-primary px-6 py-3 rounded-md font-medium" data-testid="goto-dashboard">
                {t("billingSuccess.gotoDashboard")}
              </button>
              <Link to="/new-quote" className="px-6 py-3 rounded-md bg-slate-800 text-slate-100 border border-slate-700 hover:border-blue-500 text-sm">
                {t("billingSuccess.newQuote")}
              </Link>
            </div>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div data-testid="status-failed">
            <XCircle size={48} className="text-red-400 mx-auto mb-4"/>
            <h1 className="font-display font-bold text-2xl text-slate-100">{t("billingSuccess.notCompleted")}</h1>
            <p className="text-slate-400 mt-2">{error || t("billingSuccess.sessionExpired")}</p>
            <Link to="/billing" className="mt-6 inline-block btn-primary px-6 py-3 rounded-md text-sm">{t("billingSuccess.backToBilling")}</Link>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="status-timeout">
            <Loader2 size={48} className="text-amber-400 mx-auto mb-4"/>
            <h1 className="font-display font-bold text-2xl text-slate-100">{t("billingSuccess.longer")}</h1>
            <p className="text-slate-400 mt-2">{t("billingSuccess.longerBody")}</p>
            <button onClick={() => window.location.reload()} className="mt-6 btn-primary px-6 py-3 rounded-md text-sm">{t("billingSuccess.recheck")}</button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
